import type { DriveStep } from 'driver.js';

/**
 * Candidate tour steps in sidebar order. Steps that target a `[data-tour=...]`
 * element are automatically dropped at runtime when that element isn't on the
 * page — so the tour adapts to the viewer's permissions (a resident never sees
 * the manager-only Users/Settings steps because those nav items aren't rendered
 * for them).
 *
 * ORDER MUST match config.ts navItems exactly so the highlight travels top-to-bottom
 * down the sidebar without jumping.
 */
export const TOUR_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Welcome to HOAONE 👋',
      description:
        "Here's a 60-second tour of where everything lives. You can stop anytime and replay it later from the Help center.",
    },
  },
  // ---- Main nav (matches config.ts order) ----
  {
    element: '[data-tour="nav-home"]',
    popover: {
      title: 'Home',
      description: 'Your starting point — recent announcements, upcoming items, and quick shortcuts.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-calendar"]',
    popover: {
      title: 'Calendar & Events',
      description: 'See community events and important dates in month, week, day, or agenda views.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-documents"]',
    popover: {
      title: 'Documents',
      description: 'Find forms, rules, policies, and files shared by your association.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-requests"]',
    popover: {
      title: 'Requests',
      description: 'Report maintenance issues or send questions to management, and track their status.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-announcements"]',
    popover: {
      title: 'Announcements',
      description: 'Read notices and updates from your association or management team.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-reservations"]',
    popover: {
      title: 'Reservations',
      description: 'Browse community amenities, check availability, and book the ones you need.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-discussion"]',
    popover: {
      title: 'Discussion',
      description: 'Join community conversations and start topics when input is needed.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-meetings"]',
    popover: {
      title: 'Meetings',
      description: 'Review board meeting schedules, agendas, and related materials.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-help"]',
    popover: {
      title: 'Help center',
      description:
        'Stuck on a task? The Help center has searchable, step-by-step guides — like "how to add a user." Start the tour again from here anytime.',
      side: 'right',
      align: 'start',
    },
  },
  // ---- Management section (managers / board only — auto-skipped otherwise) ----
  {
    element: '[data-tour="nav-users"]',
    popover: {
      title: 'Users',
      description: 'Add residents and staff, assign them to units, and manage roles. (Managers only.)',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-units"]',
    popover: {
      title: 'Units',
      description: 'Keep your property records — addresses, owners, and tenants — up to date. (Managers only.)',
      side: 'right',
      align: 'start',
    },
  },
  // ---- Settings section ----
  {
    element: '[data-tour="nav-community-settings"]',
    popover: {
      title: 'Community Settings',
      description:
        'Configure amenities, reservation rules, payments, and community details. (Managers and board members.)',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="nav-account-settings"]',
    popover: {
      title: 'Account Settings',
      description: 'Manage communities and account-level users. (Account admins.)',
      side: 'right',
      align: 'start',
    },
  },
  {
    popover: {
      title: "You're all set 🎉",
      description:
        'That\'s the lay of the land. Open the Help center whenever you need step-by-step instructions for a specific task.',
    },
  },
];

/**
 * Returns the steps whose target element is present in the DOM (plus any
 * element-less intro/outro steps). Run this at tour start so the walkthrough
 * matches what the current user can actually see.
 */
export function getVisibleTourSteps(): DriveStep[] {
  return TOUR_STEPS.filter((step) => {
    if (!step.element) return true;
    if (typeof step.element !== 'string') return true;
    return Boolean(document.querySelector(step.element));
  });
}
